/**
 * Helper für die Freigabe-Queue: Frist-Status + Dringlichkeit.
 *
 * Reine, DOM-freie Funktionen — testbar per Vitest und sowohl für Filter
 * (Frist-Bucket, Dringlichkeitsfilter) als auch für Badges/Sortierung in
 * EntwuerfeFreigabePage genutzt.
 */

export type FristStatus = 'ueberfaellig' | 'heute' | 'woche' | 'spaeter' | 'keine';

/** Mitternacht (lokal) eines Datums — für reine Tag-Vergleiche ohne Uhrzeit. */
function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * Klassifiziert eine Versand-Frist relativ zu `now`:
 *   - 'keine'        — keine Frist gesetzt (null/leer/ungültig)
 *   - 'ueberfaellig' — Frist-Tag liegt vor heute
 *   - 'heute'        — Frist-Tag ist heute
 *   - 'woche'        — Frist-Tag ist nach heute, aber innerhalb der nächsten 7 Tage
 *   - 'spaeter'      — Frist-Tag liegt weiter in der Zukunft
 *
 * Vergleich erfolgt tag-genau (Uhrzeit wird ignoriert), damit „heute 23:00"
 * noch als 'heute' und nicht als 'ueberfaellig' gilt.
 */
export function fristStatus(date: string | Date | null | undefined, now: Date = new Date()): FristStatus {
  if (!date) return 'keine';
  const d = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(d.getTime())) return 'keine';

  const today = startOfDay(now);
  const fristDay = startOfDay(d);
  const diffDays = Math.round((fristDay.getTime() - today.getTime()) / 86_400_000);

  if (diffDays < 0) return 'ueberfaellig';
  if (diffDays === 0) return 'heute';
  if (diffDays <= 7) return 'woche';
  return 'spaeter';
}

/** Frist-Filter-Buckets der UI. */
export type FristFilter = 'alle' | 'ueberfaellig' | 'heute' | 'woche' | 'ohne';

/** Prüft, ob ein Frist-Status zum gewählten Filter-Bucket passt. */
export function matchesFristFilter(status: FristStatus, filter: FristFilter): boolean {
  switch (filter) {
    case 'alle':
      return true;
    case 'ueberfaellig':
      return status === 'ueberfaellig';
    case 'heute':
      return status === 'heute';
    case 'woche':
      // „Diese Woche" umfasst überfällige + heutige + die nächsten 7 Tage.
      return status === 'ueberfaellig' || status === 'heute' || status === 'woche';
    case 'ohne':
      return status === 'keine';
    default:
      return true;
  }
}

export type Prioritaet = 'niedrig' | 'mittel' | 'hoch' | 'dringend';

interface PrioMeta {
  label: string;
  /** Sortier-Rang (höher = dringender). */
  rank: number;
  /** Dezente Badge-Klassen (Tailwind) im .koop-badge-Stil. */
  badgeClass: string;
}

/** Metadaten je Dringlichkeit — Label, Sortierrang, dezente Badge-Farbe. */
export const PRIO_META: Record<Prioritaet, PrioMeta> = {
  niedrig:  { label: 'Niedrig',  rank: 1, badgeClass: 'bg-gray-100 text-gray-600' },
  mittel:   { label: 'Mittel',   rank: 2, badgeClass: 'bg-blue-50 text-blue-700' },
  hoch:     { label: 'Hoch',     rank: 3, badgeClass: 'bg-orange-50 text-orange-700' },
  dringend: { label: 'Dringend', rank: 4, badgeClass: 'bg-red-50 text-red-700' },
};

/** Sortier-Rang einer (evtl. fehlenden) Dringlichkeit. */
export function prioRank(p: string | null | undefined): number {
  if (p && p in PRIO_META) return PRIO_META[p as Prioritaet].rank;
  return 0;
}

export type SortMode = 'neueste' | 'frist';

/**
 * Vergleichsfunktion für die Sortierung.
 *   - 'neueste' — created_at absteigend (Standard)
 *   - 'frist'   — früheste Frist zuerst; Einträge ohne Frist hinten,
 *                 bei Gleichstand created_at absteigend
 */
export function compareDrafts(
  a: { created_at: string; frist_versand?: string | null },
  b: { created_at: string; frist_versand?: string | null },
  mode: SortMode,
): number {
  if (mode === 'frist') {
    const fa = a.frist_versand ? new Date(a.frist_versand).getTime() : Infinity;
    const fb = b.frist_versand ? new Date(b.frist_versand).getTime() : Infinity;
    if (fa !== fb) return fa - fb;
    // Tie-Break: neueste zuerst.
  }
  return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
}
